import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import type { PetPrivateSummary, Visit, VisitActivity, VisitActivityKind } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useActiveHome } from "@/shared/session/householdContext";
import { Slot } from "@/shared/slots/Slot";
import { Button, Card, Chip, DataOriginBadge, EmptyState, ErrorState, Icon, LoadingState, Page, QueryView, TopBar, type IconName } from "@/shared/ui";
import { CafeScene } from "./CafeScene";
import { OutdoorScene } from "./OutdoorScene";
import { hasMerchantInfo, PROVIDER_TEXT, visitSetting, visitStateText, visitStory, visitSubtitle } from "./visitKind";
import "./venue.css";

/**
 * 主人能点的活动。拍照（take_photo）不给主人点：用户 10-09 定「没有拍照页，聊天里也没有拍照按钮，拍不拍由宠物自己决定」；
 * 原来这里把 visit.activities 全部画成按钮，相机那一颗主人照样能点，点了走 POST /visits/{id}/actions、花宠物那一层的生图额度（VISIT-NO-PHOTO-BTN-01，I 10-10 定）。
 * 契约（VisitActivityKind）里还有 take_photo；后端 A 同时把它从 visit.activities 撤掉（OWNER-PHOTO-ENTRY-RETIRE-01），前端先滤，后端就算还给也不出这颗按钮。
 * 图标表只列主人能点的三种，类型上仍要穷举：契约以后多一种活动、这里不补图标 tsc 就报错；没滤过的活动也传不进 ActivityButton。
 */
type OwnerActivity = VisitActivity & { kind: Exclude<VisitActivityKind, "take_photo"> };
const isOwnerActivity = (activity: VisitActivity): activity is OwnerActivity => activity.kind !== "take_photo";
const ACTIVITY_ICON: Record<OwnerActivity["kind"], IconName> = { choose_seat: "seat", order_drink: "cup", greet_resident: "heart" };

function ActivityButton({ visit, activity }: { visit: Visit; activity: OwnerActivity }) {
  const { visits } = useServices();
  const queryClient = useQueryClient();
  const keyRef = useRef(newIdempotencyKey("visit"));
  const mutation = useMutation({
    mutationFn: () => visits.act(visit.visit_id, { activity_id: activity.activity_id }, keyRef.current),
    onSuccess: (next) => {
      keyRef.current = newIdempotencyKey("visit");
      queryClient.setQueryData(queryKeys.visit(visit.visit_id), next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.collection });
      void queryClient.invalidateQueries({ queryKey: ["communicator", "messages"] });
      void queryClient.invalidateQueries({ queryKey: ["social", "feed"] });
    },
  });
  const done = activity.state === "done";
  return (
    <div className="ps-visit-act">
      <Button variant={done ? "secondary" : "primary"} icon={ACTIVITY_ICON[activity.kind]} loading={mutation.isPending} disabled={done || activity.state === "disabled"} onClick={() => mutation.mutate()}>
        <span className="ps-visit-act__label">{activity.label}</span>
      </Button>
      {activity.result_text ? <p className="ps-muted" role="status">{activity.result_text}</p> : null}
    </div>
  );
}

/**
 * 地点资料：有门店、有商家资料的才叫“商家资料”，并说明店内画面是原创场景；其余叫“关于这个地方”。
 * 没有的字段整行不显示（不写“未提供”）；来源用服务端给的中文署名，缺了才按来源类型补一句，不露 amap / world 这类代码。
 */
function PlaceCard({ visit }: { visit: Visit }) {
  const merchant = hasMerchantInfo(visit);
  const source = visit.place.attribution ?? PROVIDER_TEXT[visit.place.provider];
  return (
    <Card flat className="ps-visit-place">
      <div className="ps-section-title" style={{ marginTop: 0 }}>
        {merchant ? (visit.place.provider === "fixture" ? "商家资料（演示）" : "商家资料") : "关于这个地方"}
      </div>
      <div style={{ fontWeight: 700 }}>{visit.place.name}</div>
      {visit.place.address ? <div className="ps-muted">地址：{visit.place.address}</div> : null}
      {source ? <div className="ps-muted">{source}</div> : null}
      {merchant ? <div className="ps-muted">店内画面是动物世界的原创场景，不代表这家店的真实装修、菜单或宠物准入。</div> : null}
    </Card>
  );
}

function VisitBody({ visit, pet }: { visit: Visit; pet: PetPrivateSummary | null }) {
  const setting = visitSetting(visit);
  const seated = visit.activities.some((a) => a.kind === "choose_seat" && a.state === "done");
  const activities = visit.activities.filter(isOwnerActivity);
  return (
    <div className="ps-stack">
      {/* 画面按地点类型选：有门店的画店内，小路 / 公园画一小片户外，其他（进城逛逛、打工）不放场景图 */}
      {setting === "shop" ? <CafeScene visit={visit} pet={pet} seated={seated} /> : setting === "outdoor" ? <OutdoorScene visit={visit} pet={pet} resting={seated} /> : null}
      <div className="ps-visit-state">
        <Chip tone="leaf">{visitStateText(visit.state, setting)}</Chip>
        {pet ? <span className="ps-visit-state__story">{visitStory(setting, pet.name)}</span> : null}
        <DataOriginBadge origin={visit.data_origin} label="演示到访" />
      </div>
      {/* 滤掉拍照以后一项也不剩（比如后端只给了 take_photo）：整个活动区不渲染，不放空框、不补说明句，页面其余照旧（6c2b 10-10 定） */}
      {activities.length > 0 ? (
        <div className="ps-visit-acts">
          {activities.map((a) => (
            <ActivityButton key={a.activity_id} visit={visit} activity={a} />
          ))}
        </div>
      ) : null}
      {/* 只读的相册入口照旧；拍完以后去聊天里／家庭群看留念的那条链接随拍照按钮一起撤了（VISIT-NO-PHOTO-BTN-01） */}
      <Link className="ps-visit-memento-link" to="/album"><Icon name="album" size={17} /> 翻看已有照片 <Icon name="chevron" size={15} /></Link>
      <PlaceCard visit={visit} />
      <Slot name="venue.panels" props={{ visit }} />
      <Link to="/map" className="ps-btn ps-btn--secondary">
        <Icon name="journey" /> 回到地图
      </Link>
    </div>
  );
}

export function VisitPage() {
  const { visitId = "" } = useParams();
  const { visits } = useServices();
  const query = useQuery({ queryKey: queryKeys.visit(visitId), queryFn: () => visits.visit(visitId), refetchInterval: (state) => ["planned", "travelling", "arrived", "active"].includes(state.state.data?.state ?? "") ? 8_000 : false });
  const home = useActiveHome();
  return (
    <Page>
      <TopBar title={query.data?.place.name ?? "这次出门"} subtitle={query.data ? visitSubtitle(visitSetting(query.data)) : undefined} back="/map" />
      <QueryView query={query}>{(visit) => home.isPending ? <LoadingState label="正在确认当前宠物…" /> : home.isError ? <ErrorState error={home.error} onRetry={() => void home.refetch()} /> : home.data?.pet.pet_id !== visit.pet_id ? <EmptyState icon="lock" title="这不是当前宠物的到访">切回这次出门的伙伴，才能看见 TA 这次出门的活动。</EmptyState> : <VisitBody visit={visit} pet={home.data.pet} />}</QueryView>
    </Page>
  );
}
